import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { PoolType } from "./useProductPools";

export type EventType = "RECEIVED" | "ADJUSTMENT" | "SNAPSHOT_VARIANCE";

export interface InventoryAuditRow {
  id: string;
  effective_date: string;
  created_at: string;
  event_type: EventType;
  pool_type: PoolType;
  quantity: number;
  before_quantity: number;
  after_quantity: number;
  master_product_id: string;
  product_name: null;
  identifier_type: string;
  identifier_value: string;
  note: string | null;
  created_by_email: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface AuditLogFilters {
  organisationId: string | undefined;
  masterProductId?: string;
  poolType?: PoolType;
  eventType?: EventType;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}

export interface AuditLogPage {
  rows: InventoryAuditRow[];
  total: number;
  page: number;
  pageSize: number;
}

interface QueryResult<T> {
  data: T[] | null;
  error: { message: string } | null;
}

interface AuditLogRpcRow {
  id: string | null;
  effective_date: string | null;
  created_at: string | null;
  event_type: string;
  pool_type: string;
  quantity: number | string | null;
  before_quantity: number | string | null;
  after_quantity: number | string | null;
  master_product_id: string | null;
  identifier_type: string | null;
  identifier_value: string | null;
  note: string | null;
  created_by_email: string | null;
  metadata: Record<string, unknown> | null;
  total_count: number | string;
}

interface RpcShim {
  rpc: <T>(fn: string, args: Record<string, unknown>) => Promise<QueryResult<T>>;
}

const DEFAULT_PAGE_SIZE = 50;

export async function fetchInventoryAuditLogPage(
  client: RpcShim,
  filters: AuditLogFilters,
): Promise<AuditLogPage> {
  const {
    organisationId,
    masterProductId,
    poolType,
    eventType,
    fromDate,
    toDate,
    page = 0,
    pageSize = DEFAULT_PAGE_SIZE,
  } = filters;

  if (!organisationId) {
    return { rows: [], total: 0, page, pageSize };
  }

  const { data, error } = await client.rpc<AuditLogRpcRow>(
    "get_inventory_audit_log_page_v1",
    {
      p_organisation_id: organisationId,
      p_event_type: eventType ?? null,
      p_pool_type: poolType ?? null,
      p_master_product_id: masterProductId ?? null,
      p_from_date: fromDate ?? null,
      p_to_date: toDate ?? null,
      p_page: page,
      p_page_size: pageSize,
    },
  );

  if (error) throw new Error(error.message);

  const rows = (data ?? [])
    .filter((r) => !!r.id)
    .map<InventoryAuditRow>((r) => ({
    id: r.id,
    effective_date: r.effective_date!,
    created_at: r.created_at!,
    event_type: r.event_type as EventType,
    pool_type: r.pool_type as PoolType,
    quantity: Number(r.quantity ?? 0),
    before_quantity: Number(r.before_quantity ?? 0),
    after_quantity: Number(r.after_quantity ?? 0),
    master_product_id: r.master_product_id ?? "",
    product_name: null,
    identifier_type: r.identifier_type ?? "",
    identifier_value: r.identifier_value ?? "",
    note: r.note,
    created_by_email: r.created_by_email,
    metadata: r.metadata,
  }));

  const total = data?.[0]?.total_count ?? 0;
  return { rows, total: Number(total), page, pageSize };
}

export function useInventoryAuditLog(filters: AuditLogFilters) {
  const {
    organisationId,
    masterProductId,
    poolType,
    eventType,
    fromDate,
    toDate,
    page = 0,
    pageSize = DEFAULT_PAGE_SIZE,
  } = filters;

  return useQuery({
    queryKey: [
      "inventory-audit-log",
      organisationId,
      masterProductId,
      poolType,
      eventType,
      fromDate,
      toDate,
      page,
      pageSize,
    ],
    enabled: !!organisationId,
    placeholderData: keepPreviousData,
    queryFn: (): Promise<AuditLogPage> =>
      fetchInventoryAuditLogPage(supabase as unknown as RpcShim, filters),
  });
}
